import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Key, Copy, Check, Info, ShieldCheck, X } from 'lucide-react';

export const ParentEnrollmentModal = ({ parentId, parentName, onClose, backendUrl }) => {
  const [payload, setPayload] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('qr'); // 'qr' or 'manual'
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);
        // Simulate network delay
        await new Promise(r => setTimeout(r, 600));
        
        // Generate a secure mock payload for parent app
        const data = {
          server_url: backendUrl,
          parent_id: parentId,
          enrollment_token: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
        };
        
        setTokenData(data);
        setPayload(JSON.stringify(data));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchToken();
  }, [parentId, backendUrl]);

  const getActivationCode = () => {
    if (!tokenData) return '';
    try {
      const compactObj = {
        server_url: tokenData.server_url,
        parent_id: tokenData.parent_id,
        enrollment_token: tokenData.enrollment_token
      };
      return btoa(JSON.stringify(compactObj));
    } catch (e) {
      return payload || '';
    }
  };

  const handleCopy = async (text, key) => {
    if (!text) return;
    const strText = String(text);
    let copied = false;

    // 1. Try modern navigator.clipboard if supported and available (HTTPS or localhost)
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(strText);
        copied = true;
      } catch (err) {
        console.warn('navigator.clipboard.writeText failed, using fallback', err);
        copied = false;
      }
    }

    // 2. Fallback to document.execCommand('copy') for non-secure origins (e.g. HTTP on local network IP 192.168.x.x)
    if (!copied) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = strText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.width = '2em';
        textarea.style.height = '2em';
        textarea.style.padding = '0';
        textarea.style.border = 'none';
        textarea.style.outline = 'none';
        textarea.style.boxShadow = 'none';
        textarea.style.background = 'transparent';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        textarea.style.zIndex = '-9999';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, 99999);
        copied = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch (err) {
        console.error('Fallback execCommand copy failed', err);
      }
    }

    if (copied) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const activationCode = getActivationCode();

  return (
    <div style={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>Enroll Parent Device</h3>
            <p style={styles.subtitle}>{parentName || `Parent #${parentId}`}</p>
          </div>
          <button onClick={onClose} style={styles.iconCloseBtn} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Tab Switcher */}
        <div style={styles.tabBar}>
          <button
            style={{
              ...styles.tabBtn,
              ...(activeTab === 'qr' ? styles.tabBtnActive : {})
            }}
            onClick={() => setActiveTab('qr')}
          >
            <QrCode size={16} />
            <span>Scan QR Code</span>
          </button>
          <button
            style={{
              ...styles.tabBtn,
              ...(activeTab === 'manual' ? styles.tabBtnActive : {})
            }}
            onClick={() => setActiveTab('manual')}
          >
            <Key size={16} />
            <span>Enter Manually</span>
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
              Generating secure single-use activation credentials...
            </p>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <p style={{ color: '#ef4444', fontWeight: '600' }}>{error}</p>
          </div>
        ) : activeTab === 'qr' ? (
          /* QR Code View */
          <div style={styles.qrContainer}>
            <div style={styles.qrCard}>
              <QRCodeSVG value={payload} size={220} level="H" includeMargin />
            </div>
            <p style={styles.hint}>
              Instruct the parent to open the <strong>Parent App</strong> and scan this QR code with the camera.
            </p>
            <div style={styles.cantScanBanner}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Parent camera not working or remote?</span>
              <button
                type="button"
                style={styles.switchTabLink}
                onClick={() => setActiveTab('manual')}
              >
                View Manual Activation Code &rarr;
              </button>
            </div>
          </div>
        ) : (
          /* Manual Code View */
          <div style={styles.manualContainer}>
            <div style={styles.infoBanner}>
              <Info size={16} color="#0f766e" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#0f766e', lineHeight: 1.4 }}>
                If the parent is unable to scan the QR code, they can tap <strong>"Can't scan? Enter code manually"</strong> on the Parent App and paste this code.
              </div>
            </div>

            {/* Quick Full Activation Code */}
            <div style={styles.codeBlockWrap}>
              <div style={styles.codeBlockHeader}>
                <span style={styles.codeBlockLabel}>FULL ACTIVATION CODE</span>
                <button
                  type="button"
                  style={styles.copyBtn}
                  onClick={() => handleCopy(activationCode, 'full')}
                >
                  {copiedKey === 'full' ? (
                    <>
                      <Check size={14} color="#16a34a" />
                      <span style={{ color: '#16a34a' }}>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      <span>Copy Full Code</span>
                    </>
                  )}
                </button>
              </div>
              <textarea
                readOnly
                value={activationCode}
                style={{ ...styles.codeTextarea, cursor: 'pointer' }}
                rows={3}
                onClick={(e) => {
                  e.target.select();
                  handleCopy(activationCode, 'full');
                }}
                title="Click to copy full code"
              />
            </div>

            {/* Individual Credential Breakdown */}
            <div style={styles.detailsGroup}>
              <span style={styles.detailsGroupTitle}>OR ENTER INDIVIDUAL DETAILS</span>
              
              <div style={styles.detailRow}>
                <div style={styles.detailLabelCol}>
                  <span style={styles.detailLabel}>Server URL</span>
                  <span style={styles.detailValue}>{tokenData?.server_url}</span>
                </div>
                <button
                  type="button"
                  style={styles.miniCopyBtn}
                  onClick={() => handleCopy(tokenData?.server_url, 'url')}
                  title="Copy Server URL"
                >
                  {copiedKey === 'url' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                </button>
              </div>

              <div style={styles.detailRow}>
                <div style={styles.detailLabelCol}>
                  <span style={styles.detailLabel}>Parent ID</span>
                  <span style={styles.detailValue}>{tokenData?.parent_id}</span>
                </div>
                <button
                  type="button"
                  style={styles.miniCopyBtn}
                  onClick={() => handleCopy(String(tokenData?.parent_id), 'parent_id')}
                  title="Copy Parent ID"
                >
                  {copiedKey === 'parent_id' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                </button>
              </div>

              <div style={styles.detailRow}>
                <div style={styles.detailLabelCol}>
                  <span style={styles.detailLabel}>Activation Token</span>
                  <span style={styles.detailValueCode}>{tokenData?.enrollment_token}</span>
                </div>
                <button
                  type="button"
                  style={styles.miniCopyBtn}
                  onClick={() => handleCopy(tokenData?.enrollment_token, 'token')}
                  title="Copy Activation Token"
                >
                  {copiedKey === 'token' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.securityNote}>
            <ShieldCheck size={14} color="#10b981" />
            <span>Single-use token • Valid for 24 hours</span>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>Close</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    backdropFilter: 'blur(3px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '16px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    maxWidth: '460px',
    width: '100%',
    color: '#0f172a',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px 16px 24px',
    borderBottom: '1px solid #f1f5f9'
  },
  title: {
    margin: 0,
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#0f172a'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '0.85rem',
    color: '#0f766e',
    fontWeight: '600'
  },
  iconCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  tabBar: {
    display: 'flex',
    padding: '0 24px',
    borderBottom: '1px solid #e2e8f0',
    gap: '8px'
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  tabBtnActive: {
    color: '#0f766e',
    borderBottomColor: '#0f766e'
  },
  loadingContainer: {
    padding: '40px 24px',
    textAlign: 'center'
  },
  spinner: {
    width: '32px',
    height: '32px',
    margin: '0 auto',
    border: '3px solid #e2e8f0',
    borderTopColor: '#0f766e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    padding: '30px 24px',
    textAlign: 'center'
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    gap: '16px'
  },
  qrCard: {
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
  },
  hint: {
    margin: 0,
    fontSize: '0.85rem',
    color: '#475569',
    textAlign: 'center',
    lineHeight: 1.4
  },
  cantScanBanner: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    width: '100%',
    textAlign: 'center'
  },
  switchTabLink: {
    background: 'none',
    border: 'none',
    color: '#0f766e',
    fontWeight: '700',
    fontSize: '12px',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  manualContainer: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    maxHeight: '400px',
    overflowY: 'auto'
  },
  infoBanner: {
    display: 'flex',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: '#f0fdfa',
    borderRadius: '8px',
    border: '1px solid #ccfbf1'
  },
  codeBlockWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  codeBlockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  codeBlockLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    letterSpacing: '0.5px'
  },
  copyBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#0f766e',
    backgroundColor: '#f0fdfa',
    border: '1px solid #ccfbf1',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  codeTextarea: {
    width: '100%',
    fontFamily: 'monospace',
    fontSize: '11px',
    backgroundColor: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '8px 10px',
    color: '#1e293b',
    resize: 'none',
    boxSizing: 'border-box'
  },
  detailsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0'
  },
  detailsGroupTitle: {
    fontSize: '10px',
    fontWeight: '800',
    color: '#64748b',
    letterSpacing: '0.6px',
    marginBottom: '2px'
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #edf2f7'
  },
  detailLabelCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    marginRight: '8px',
    overflow: 'hidden'
  },
  detailLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase'
  },
  detailValue: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailValueCode: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#0f172a',
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  miniCopyBtn: {
    padding: '6px',
    backgroundColor: '#f1f5f9',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #f1f5f9'
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: '#64748b',
    fontWeight: '600'
  },
  closeBtn: {
    padding: '8px 18px',
    backgroundColor: '#0f172a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '700'
  }
};


